/**
 * File Page
 * Shows the HTML content of a selected file
 */

import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Download, Printer } from 'lucide-react';
import { ChevronLeft } from 'lucide-react';
import { useAppContext } from '../contexts/AppContext';

const preprocessContent = (html) => {
  return html.replace(
    /(<p[^>]*>)([\s\S]*?)(<\/p>)/g,
    (match, open, inner, close) => {
      const rawText = inner.replace(/<[^>]*>/g, '').trim();
      if (
        rawText.startsWith('״') ||
        rawText.startsWith('\u05F4') ||
        rawText.startsWith("''") ||
        rawText.startsWith(',,') ||
        rawText.startsWith('"')
      ) {
        const newOpen = open.includes('style=')
          ? open.replace(/style="/, 'style="margin-right: 40px; margin-left: 0; border-right: 3px solid #c0a060; padding-right: 16px; color: #444; ')
          : open.replace('>', ' style="margin-right: 40px; margin-left: 0; border-right: 3px solid #c0a060; padding-right: 16px; color: #444;">');
        return `${newOpen}${inner}${close}`;
      }
      return match;
    }
  );
};

const createStyledHtml = (content, fontSize) => {
  return `
    <html>
      <head>
        <meta charset="UTF-8" />
        <base target="_self" />
        <link
          href="https://fonts.googleapis.com/css2?family=Frank+Ruhl+Libre:wght@400;700&family=Noto+Serif+Hebrew&family=David+Libre&display=swap"
          rel="stylesheet"
        />
        <style>
          body {
            font-family: 'Frank Ruhl Libre', 'Noto Serif Hebrew', 'David Libre', serif;
            font-size: ${fontSize}%;
            line-height: 2.2;
            margin: 0;
            padding: 32px;
            color: #333333;
            background-color: #fdf8f0;
            direction: rtl;
            text-align: justify;
          }
          h1, h2, h3, h4, h5, h6 {
            color: #1A237E;
            font-weight: bold;
            text-align: right;
            margin-bottom: 16px;
          }
          h1 { font-size: 2em; }
          h2 { font-size: 1.6em; }
          h3 { font-size: 1.3em; }
          p {
            margin-bottom: 16px;
            text-align: justify;
          }
          p:first-of-type {
            font-size: 1.1em;
            font-weight: bold;
          }
          table {
            border-collapse: collapse;
            width: 100%;
            margin: 16px 0;
          }
          td, th {
            padding: 8px;
            border: 1px solid #ddd;
          }
          strong { font-weight: bold; }
          em { font-style: italic; }
          u { text-decoration: underline; }
          a { color: #3498db; text-decoration: none; }
          blockquote {
            border-right: 4px solid #ddd;
            padding-right: 16px;
            margin: 16px 0;
            font-style: italic;
          }
          .search-highlight {
            background-color: #fff176;
            padding: 2px 4px;
            border-radius: 3px;
          }

          @page {
            size: A4;
            margin: 15mm 20mm;
          }
          @media print {
            body {
              padding: 0;
              background-color: #ffffff;
            }
            p, h1, h2, h3, h4, h5, h6, table, blockquote, ul, ol, li {
              page-break-inside: avoid;
              break-inside: avoid;
            }
            h1, h2, h3, h4, h5, h6 {
              page-break-after: avoid;
              break-after: avoid;
            }
          }
        </style>
      </head>
      <body>
        ${content}
        <script>
          document.addEventListener('click', function(e) {
            const link = e.target.closest('a');
            if (!link) return;
            const href = link.getAttribute('href');
            if (href && href.startsWith('#')) {
              e.preventDefault();
              const target = document.getElementById(href.slice(1));
              if (target) target.scrollIntoView({ behavior: 'smooth' });
            } else if (href) {
              e.preventDefault();
              window.open(href, '_blank');
            }
          });
        <\/script>
      </body>
    </html>
  `;
};

const FilePage = () => {
  const { bookId, parshaName, fileName } = useParams();
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const {
    manifest,
    books,
    selectedBook,
    selectedParsha,
    selectedFile,
    content,
    fontSize,
    setFontSize,
    loading,
    setSelectedBook,
    setSelectedParsha,
    setSelectedFile
  } = useAppContext();
  const [isContactOpen, setIsContactOpen] = useState(false);
  useEffect(() => {
    // Wait until the manifest has loaded so books contain full parsha/file data.
    // While loading, or before the manifest arrives, do nothing — the spinner
    // below will be shown in the meantime.
    if (loading || !manifest) {
      return;
    }

    const book = books.find(b => b.id === bookId);

    if (!book) {
      navigate('/');
      return;
    }
    const decodedParshaName = decodeURIComponent(parshaName);
    const parsha = book.parshiot?.find(p => p.name.trim() === decodedParshaName.trim());

    if (!parsha) {
      navigate(`/book/${bookId}`);
      return;
    }

    const decodedFileName = decodeURIComponent(fileName);
    const normalizedSearch = decodedFileName.replace(/\.html$/i, '').trim();

    // Try exact match first (normalized)
    let file = parsha.files?.find(f => f.replace(/\.html$/i, '').trim() === normalizedSearch);

    // Fallback: match by leading number prefix (e.g. "7.") — the Lambda sometimes
    // returns truncated or slightly different titles than what's stored in the manifest
    if (!file) {
      const prefixMatch = normalizedSearch.match(/^(\d+\.)/);
      if (prefixMatch) {
        const prefix = prefixMatch[1];
        file = parsha.files?.find(f => f.startsWith(prefix));
      }
    }

    if (!file) {
      navigate(`/book/${bookId}/parsha/${parshaName}`);
      return;
    }

    if (!selectedBook || selectedBook.id !== bookId) {
      setSelectedBook(book);
    }
    if (!selectedParsha || selectedParsha.name !== parsha.name) {
      setSelectedParsha(parsha);
    }
    if (!selectedFile || selectedFile !== file) {
      setSelectedFile(file);
    }
  }, [bookId, parshaName, fileName, manifest, loading, books, selectedBook, selectedParsha, selectedFile, setSelectedBook, setSelectedParsha, setSelectedFile, navigate]);

  const getFileDisplayName = () => {
    if (!selectedFile) return '';
    return selectedFile.replace('.html', '');
  };

  const handlePrint = async () => {
    if (!selectedBook || !selectedParsha || !selectedFile) return;

    const encodeS3Part = (str) =>
      encodeURIComponent(str).replace(/%20/g, '+').replace(/%27/g, "'");

    const filename = getFileDisplayName() + '.pdf';
    const fixedFilename = filename.replace(/(שנה [א-ת])/g, "$1'");
    const capitalized = selectedBook.id.charAt(0).toUpperCase() + selectedBook.id.slice(1);

    const fileUrl =
      `https://${process.env.REACT_APP_S3_BUCKET}.s3.us-east-1.amazonaws.com/books/` +
      `${encodeS3Part(capitalized)}/` +
      `${encodeS3Part(selectedParsha.name)}/` +
      `${encodeS3Part(fixedFilename)}`;

    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error('PDF not found on S3');

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const printIframe = document.createElement('iframe');
      printIframe.style.display = 'none';
      printIframe.src = blobUrl;
      document.body.appendChild(printIframe);

      printIframe.onload = () => {
        printIframe.contentWindow.focus();
        printIframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(printIframe);
          URL.revokeObjectURL(blobUrl);
        }, 1000);
      };
    } catch (err) {
      console.error('Print from S3 failed, falling back to HTML print:', err);
      const iframe = iframeRef.current;
      if (!iframe) return;
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  };

  const handleDownload = async () => {
  if (isDownloading) return;
  setIsDownloading(true);
  try {
    const filename = getFileDisplayName() + '.pdf';
    const fixedFilename = filename.replace(/(שנה [א-ת])/g, "$1'");
    const bookName = selectedBook.id;
    const capitalized =
  bookName.charAt(0).toUpperCase() + bookName.slice(1);
    const encodeS3Part = (str) => {
  return encodeURIComponent(str)
    .replace(/%20/g, '+')    // spaces as +
    .replace(/%27/g, "'");   // literal apostrophe
};

const fileUrl =
  `https://${process.env.REACT_APP_S3_BUCKET}.s3.us-east-1.amazonaws.com/books/` +
  `${encodeS3Part(capitalized)}/` +
  `${encodeS3Part(selectedParsha.name)}/` +
  `${encodeS3Part(fixedFilename)}`;

    const response = await fetch(fileUrl);
    if (!response.ok) throw new Error('PDF not found on S3');

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fixedFilename;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Download failed:', err);
    // fallback to print
    handlePrint();
  } finally {
    setIsDownloading(false);
  }
};

  if (!selectedBook || !selectedParsha || !selectedFile) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-white">טוען...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center">
      {/* TOP NAV BAR */}
      <div dir="rtl" className="w-full py-4 px-6 flex justify-between items-center" >
      
      {/* Contact */}
      <div className="relative">
        <button 
          onClick={() => setIsContactOpen(!isContactOpen)}
          className="flex items-center gap-2 text-amber-300 hover:text-amber-400 transition font-semibold text-base"
        >
          <span>צור קשר</span>
          <span className={`text-[11px] transition-transform ${isContactOpen ? 'rotate-180' : ''}`}>▼</span>
        </button>

        {isContactOpen && (
          <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl p-2 z-50 text-gray-800 border border-gray-200">
            <a href="mailto:tora2338@gmail.com" className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition">
              <span className="text-xl">📧</span>
              <div className="flex flex-col text-right">
                <span className="font-bold text-sm">דברו איתנו במייל</span>
                <span className="text-[11px] text-gray-500">להערות, הארות, לקבלת הדף השבועי במייל הקדשה לעילוי נשמת וכו׳</span>
              </div>
            </a>
            <a  
              href="https://links.payboxapp.com/iBta1etlyWb" 
              target="_blank" 
              rel="noreferrer" 
              className="flex items-center gap-3 p-3 hover:bg-orange-50 rounded-lg transition border-t mt-1"
            >
              <span className="text-xl">💰</span>
              <div className="flex flex-col text-right">
                <span className="font-bold text-sm text-orange-700">תרומה בפייבוקס</span>
                <span className="text-[11px] text-gray-500">עזרו לנו לגדול</span>
              </div>
            </a>
          </div>
        )}
      </div>

      {/* Logo / Title center */}
      <div className="absolute left-1/2 -translate-x-1/2 text-center hidden md:block">
        <span className="text-amber-300 font-bold text-lg md:text-xl tracking-wide">📖 הלכה בפרשה</span>
      </div>

      {/* App Link */}
      <a 
        href="https://play.google.com/store/apps/details?id=com.halachaparasha.app" 
        target="_blank" 
        rel="noreferrer" 
        className="font-semibold text-base text-amber-300 hover:text-amber-400 transition"
      >
        מעבר לאפליקציה 📱
      </a>
      </div>
      {/* Main container */}
      <div className="w-full max-w-4xl rounded-3xl shadow-2xl border-2 border-amber-200 p-8 md:p-12" style={{ backgroundColor: '#ffffff' }}>        <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-amber-200 flex-wrap gap-4">
          <button
            onClick={() => window.history.length > 2 ? navigate(-1) : navigate('/')}
            className="flex items-center gap-2 mb-4 text-blue-600 font-semibold hover:text-blue-800"
          >
            <ChevronLeft className="w-5 h-5" /> חזור
          </button>
          {/* Download & Print Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownload}
              disabled={isDownloading}
              className="flex items-center gap-2 bg-amber-700 text-white px-4 py-2 rounded-lg hover:bg-amber-800 transition-all duration-200 hover:scale-105 shadow-md font-semibold disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <Download className="w-4 h-4" />
              {isDownloading ? 'מכין...' : 'הורד PDF'}
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all duration-200 hover:scale-105 shadow-md font-semibold"
            >
              <Printer className="w-4 h-4" />
              הדפס
            </button>
          </div>

          {/* Font Size Controls */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setFontSize(Math.max(50, fontSize - 10))}
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-all duration-200 font-bold"
            >
              A-
            </button>
            <span className="font-bold text-gray-700 min-w-[120px] text-center">
              גודל טקסט: {fontSize}%
            </span>
            <button
              onClick={() => setFontSize(Math.min(200, fontSize + 10))}
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-all duration-200 font-bold"
            >
              A+
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">טוען תוכן...</p>
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            id="content-display"
            className="w-full rounded-xl shadow-lg bg-white"
            style={{ minHeight: '600px', border: 'none' }}
            srcDoc={createStyledHtml(preprocessContent(content), fontSize)}
            title={getFileDisplayName()}
          />
        )}
      </div>

      {/* Bottom spacer */}
      <div className="h-16"></div>
    </div>
  );
};

export default FilePage;